import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Lock, PauseCircle } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { getMyProfile, removeRepresentativeToken } from '../../services/representativeService';
import { isCircolariDisattivate } from '../../services/circolariService';
import { circolariRappresentanteApi, isSenzaPermesso } from '../../services/circolariRappresentanteService';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import ElencoCircolari from '../../components/circolari/ElencoCircolari';

/**
 * Archivio circolari nell'area associativa (rappresentanti con il permesso per
 * carica associativa.circolari.read): tutte le circolari pubblicate. Stessi
 * filtri ed elenco dell'area riservata (components/circolari/ElencoCircolari).
 */

export interface RepProfileCircolari {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  caricaPrincipale?: string;
  foto?: string;
  associatesAccess?: boolean;
  permissions: { name: string; description: string }[];
}

export type AccessoCircolari = 'ok' | 'senza-permesso' | 'disattivate';

/** Profilo del rappresentante per la shell (pattern comune dell'area associativa). */
export const useProfiloRappresentante = (): RepProfileCircolari | null => {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useState<RepProfileCircolari | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }
    const loadProfile = async () => {
      try {
        const res = await getMyProfile();
        if (res.success) setProfile(res.data);
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore caricamento profilo');
      }
    };
    loadProfile();
  }, [navigate, toast]);

  return profile;
};

/** Gestore degli errori di accesso alle circolari per i componenti condivisi. */
export const gestoreAccessoCircolari = (setAccesso: (a: AccessoCircolari) => void) => (error: any): boolean => {
  if (isCircolariDisattivate(error)) { setAccesso('disattivate'); return true; }
  if (isSenzaPermesso(error)) { setAccesso('senza-permesso'); return true; }
  return false;
};

/** Shell dell'area associativa con la voce «Circolari» attiva. */
export const ShellCircolari: React.FC<{ profile: RepProfileCircolari | null; children: React.ReactNode }> = ({ profile, children }) => {
  const canLive = profile?.permissions?.some(p => p.name === 'associativa.live.publish') ?? false;
  const canCircolari = profile?.permissions?.some(p => p.name === 'associativa.circolari.read') ?? false;
  const canSondaggi = profile?.permissions?.some(p => p.name === 'associativa.sondaggi.read') ?? false;
  const canCalendario = profile?.permissions?.some(p => p.name === 'associativa.eventi.read') ?? false;
  return (
    <AssociativeAppShell
      route="circolari"
      nome={profile?.nome}
      cognome={profile?.cognome}
      caricaPrincipale={profile?.caricaPrincipale}
      tipo={profile?.tipo}
      foto={profile?.foto}
      associatesAccess={profile?.associatesAccess}
      canLive={canLive}
      canCircolari={canCircolari}
      canSondaggi={canSondaggi}
      canCalendario={canCalendario}
    >
      {children}
    </AssociativeAppShell>
  );
};

/** Avviso in card per 403 (cariche non abilitate) e 503 (modulo spento). */
export const AvvisoAccessoCircolari: React.FC<{ accesso: Exclude<AccessoCircolari, 'ok'> }> = ({ accesso }) => {
  const navigate = useNavigate();
  const senzaPermesso = accesso === 'senza-permesso';
  return (
    <section className="card">
      <div className="card-head">
        <FileText size={16} />
        <h2>Circolari</h2>
      </div>
      <div className="card-body">
        <div role={senzaPermesso ? 'alert' : 'status'} className="text-center px-4 py-10">
          <span className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center bg-gray-100 dark:bg-gray-700">
            {senzaPermesso
              ? <Lock className="w-5 h-5 text-gray-500 dark:text-gray-300" aria-hidden="true" />
              : <PauseCircle className="w-5 h-5 text-gray-500 dark:text-gray-300" aria-hidden="true" />}
          </span>
          <p className="text-base font-semibold text-gray-900 dark:text-white">
            {senzaPermesso
              ? 'Le tue cariche non sono abilitate a consultare le circolari'
              : 'Le circolari sono temporaneamente disattivate'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 max-w-md mx-auto">
            {senzaPermesso
              ? 'Per l\'accesso all\'archivio rivolgiti alla segreteria UNASCA.'
              : 'L\'archivio delle circolari al momento non è disponibile. Riprova più tardi.'}
          </p>
          {senzaPermesso && (
            <button
              type="button"
              onClick={() => navigate('/area-associativa')}
              className="btn primary"
              style={{ marginTop: 20 }}
            >
              Torna alla dashboard
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

const linkDettaglio = (id: string) => `/area-associativa/circolari/${id}`;

const AssociativeCircolari: React.FC = () => {
  const profile = useProfiloRappresentante();
  const [accesso, setAccesso] = useState<AccessoCircolari>('ok');
  const onErroreAccesso = gestoreAccessoCircolari(setAccesso);

  return (
    <ShellCircolari profile={profile}>
      <div className="page fade-in">
        {accesso !== 'ok' ? (
          <AvvisoAccessoCircolari accesso={accesso} />
        ) : (
          <section className="card">
            <div className="card-head">
              <FileText size={16} />
              <h2>Circolari</h2>
            </div>
            <div className="card-body">
              <ElencoCircolari
                api={circolariRappresentanteApi}
                linkDettaglio={linkDettaglio}
                onErroreAccesso={onErroreAccesso}
              />
            </div>
          </section>
        )}
      </div>
    </ShellCircolari>
  );
};

export default AssociativeCircolari;
